import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { InventoryTree, WelfareType } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';
import { unique } from 'remeda';

import { StyledCardWrapperWithState } from '@/components';
import { useGetFishObservations } from '@/dataAccess/rest/orbitOneGateway';
import { responsive } from '@/utils';

import { FishObservationGallery } from './orbitOneImageGalleryPanel/FishObservationGallery.tsx';
import {
  welfareFilterClasses,
  welfareFilterMapping,
} from './orbitOneImageGalleryPanel/fishObservationGallery/fishObservationGallery.constants.ts';
import {
  filterLiceGalleryObservations,
  isLiceFilterClass,
} from './orbitOneImageGalleryPanel/fishObservationGallery/fishObservationGallery.functions.ts';
import type {
  OrbitOneGalleryFilterClass,
  OrbitOneGalleryFilterState,
} from './orbitOneImageGalleryPanel/fishObservationGallery/fishObservationGallery.types.ts';
import { OrbitOneImageGalleryFilters } from './orbitOneImageGalleryPanel/OrbitOneImageGalleryFilters.tsx';

interface OrbitOneImageGalleryPanelProps {
  camera: InventoryTree;
  filters: OrbitOneGalleryFilterState;
  availableFilterClasses: OrbitOneGalleryFilterClass[];
  onFiltersChange: (filters: OrbitOneGalleryFilterState) => void;
}

/** Shows the filter toolbar and observation status for a single sensor camera. */
export const OrbitOneImageGalleryPanel = ({
  camera,
  filters: { date, filters: appliedFilters },
  availableFilterClasses,
  onFiltersChange,
}: OrbitOneImageGalleryPanelProps) => {
  const { t } = useTranslation('orbitOne');

  const { data, isLoading, isError } = useGetFishObservations({
    inventoryId: camera.id,
    date,
    // Every lice class collapses to `WelfareType.Lice`, so distinct selections repeat it.
    welfare: [WelfareType.Lice, ...welfareFilterClasses],
    filter: unique(
      appliedFilters.flatMap(filter =>
        isLiceFilterClass(filter) ? [filter] : welfareFilterMapping[filter]
      )
    ),
  });

  // Only the lice gallery narrows the gateway result client-side (scale loss > 0).
  const isLiceGallery = availableFilterClasses.some(isLiceFilterClass);
  const observations = useMemo(
    () =>
      isLiceGallery
        ? filterLiceGalleryObservations({ observations: data?.fish ?? [], filters: appliedFilters })
        : (data?.fish ?? []),
    [data?.fish, isLiceGallery, appliedFilters]
  );

  return (
    <FlexAligner direction='column' fullHeight style={{ alignItems: 'stretch' }}>
      <OrbitOneImageGalleryFilters
        availableFilterClasses={availableFilterClasses}
        filters={appliedFilters}
        onFiltersChange={prevFilters => onFiltersChange({ date, filters: prevFilters })}
        date={date}
        onDateChange={date => onFiltersChange({ filters: appliedFilters, date })}
      />

      <StyledCardWrapperWithState
        hideBorder={true}
        isLoading={isLoading}
        isError={isError}
        borderRadius={'0'}
        elevation='none'
        surfaceColor={'transparent'}
        errorMessage={t('liceCount.imageGallery.cannotLoadObservations')}
        padding={responsive.spacing['2xlarge']}
        style={{ flex: 1, minHeight: 0 }}
      >
        <FishObservationGallery
          observations={observations}
          availableFilterClasses={availableFilterClasses}
          filters={appliedFilters}
          isLoading={isLoading}
        />
      </StyledCardWrapperWithState>
    </FlexAligner>
  );
};
